import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
    FaFileInvoice, FaFileAlt, FaTrashAlt, FaSearch, FaArrowLeft,
    FaUpload, FaComments, FaFileDownload, FaFolder, FaEdit, FaArrowRight, FaHome,
    FaEllipsisV, FaFolderPlus, FaUserShield, FaTimes, FaChevronRight, FaEye, FaCopy, FaClipboard
} from "react-icons/fa";
import FilePreviewModal from "./FilePreviewModal";
import PaymentDetailsModal from "./PaymentDetailsModal";

const SYSTEM_DEFAULT_FOLDERS = ["Post Formation documents", "Tax ID (EIN)"];
const TAB_SECTION_MAP = {
    documents: "documents",
    invoices: "invoices",
    "chat-documents": "chat",
};
const SECTION_LABELS = {
    documents: "Documents",
    invoices: "Invoices",
    chat: "Chat",
};
const SECTION_TRANSFER_RULES = {
    document: { documents: true, invoices: true, chat: false },
    invoice: { documents: true, invoices: true, chat: false },
    "chat-document": { documents: true, invoices: true, chat: true },
};
const ITEM_TYPE_LABELS = {
    document: "Documents",
    invoice: "Invoices",
    "chat-document": "Chat files",
};
const getFolderSection = (folder) => folder?.section || "documents";

const sortByDateDesc = (items = []) =>
  [...items].sort((a, b) => {
    const dateA = new Date(a.date || a.createdAt || 0).getTime();
    const dateB = new Date(b.date || b.createdAt || 0).getTime();
    return dateB - dateA;
  });

const DND_MIME = "application/x-docs-manager-items";
const itemKey = (item) =>
    item.type === "chat-document" && item.fileIndex != null
        ? `${item.type}:${item.id}:${item.fileIndex}`
        : `${item.type}:${item.id}`;

const DocumentsManager = ({ userId, userName: initialUserName, hideBackButton = false }) => {
    const navigate = useNavigate();
    const cardClass = "bg-white dark:bg-dark-800/60 rounded-xl border border-gray-200 dark:border-dark-600 shadow-sm overflow-hidden";
    const folderCardClass = "bg-white dark:bg-dark-800/60 rounded-xl border border-gray-200 dark:border-dark-600 shadow-sm overflow-visible";
    const cardBodyClass = "p-4";
    const labelClass = "text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
    const inputClass = "w-full px-3 py-2.5 text-sm bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500 outline-none transition-all";
    const tabBtnClass = (active) =>
        `flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${
            active
                ? "bg-primary-600 text-white shadow-sm"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700"
        }`;
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [documentFile, setDocumentFile] = useState(null);
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [registeredAgentFile, setRegisteredAgentFile] = useState(null);
    const [userInvoices, setUserInvoices] = useState([]);
    const [userDocuments, setUserDocuments] = useState([]);
    const [chatDocuments, setChatDocuments] = useState([]);
    const [registeredAgentDocuments, setRegisteredAgentDocuments] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [folderPath, setFolderPath] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState({ type: "", id: "" });
    const [itemToMove, setItemToMove] = useState({ type: "", id: "", name: "" });
    const [itemToRename, setItemToRename] = useState({ type: "", id: "", name: "" });
    const [activeTab, setActiveTab] = useState("documents");
    const [searchTerm, setSearchTerm] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [newFolderName, setNewFolderName] = useState("");
    const [newItemName, setNewItemName] = useState("");
    const [targetFolder, setTargetFolder] = useState("");
    const [transferAction, setTransferAction] = useState("move");
    const [targetSection, setTargetSection] = useState("documents");
    const [previewFile, setPreviewFile] = useState(null);
    const [forceDelete, setForceDelete] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [clipboard, setClipboard] = useState(null);
    const [dragOverTarget, setDragOverTarget] = useState(null);
    const [isBatchTransferring, setIsBatchTransferring] = useState(false);


    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const fetchChatDocuments = async (token) => {
        const chatDocResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/chat/documents?userId=${userId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        const activeChatDocs = chatDocResponse.ok ? await chatDocResponse.json() : [];

        const yearsResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/chat/available-years?userId=${userId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );

        let archivedChatDocs = [];
        if (yearsResponse.ok) {
            const years = await yearsResponse.json();
            for (const yearData of years) {
                const archivedDocsResponse = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-documents?userId=${userId}&year=${yearData.year}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (archivedDocsResponse.ok) {
                    const yearDocs = await archivedDocsResponse.json();
                    archivedChatDocs = [...archivedChatDocs, ...yearDocs];
                }
            }
        }

        setChatDocuments([...activeChatDocs, ...archivedChatDocs]);
    };

    const fetchUserDocuments = async () => {
        if (!userId) return;
        const token = localStorage.getItem("employeeToken");
        if (!token) {
            navigate("/login");
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (!response.ok) throw new Error("Failed to fetch user documents");

            const data = await response.json();
            setUser(data.user);
            setUserInvoices(data.user.invoices || []);
            setUserDocuments(data.user.documents || []);
            setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
            setFolders(data.user.folders || []);

            await fetchChatDocuments(token);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserDocuments();
        const interval = setInterval(fetchUserDocuments, 30000);
        return () => clearInterval(interval);
    }, [userId]);

    const handleFileChange = (e, type) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const fileList = Array.from(files).map(file => ({
            name: file.name,
            size: file.size,
            type: file.type,
            file
        }));

        setSelectedFiles(fileList);

        if (type === 'invoice') {
            setInvoiceFile(files);
        } else if (type === 'registered-agent') {
            setRegisteredAgentFile(files);
        } else {
            setDocumentFile(files);
        }
    };

    const performInvoiceUpload = async (paymentData = null) => {
        setShowPaymentModal(false);
        const token = localStorage.getItem("employeeToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (invoiceFile instanceof FileList) {
            Array.from(invoiceFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        if (paymentData) {
            const { sendEmail, ...details } = paymentData;
            const paidAmount = details.amount !== '' && details.amount != null ? Number(details.amount) : null;
            const hasPaymentDetails = details.service && paidAmount != null && !Number.isNaN(paidAmount);

            if (hasPaymentDetails) {
                formData.append("paymentDetails", JSON.stringify({
                    ...details,
                    amount: paidAmount,
                    amountUSD: details.amountUSD !== '' && details.amountUSD != null
                        ? Number(details.amountUSD)
                        : paidAmount,
                }));
            }
            formData.append("sendEmail", sendEmail !== undefined ? sendEmail : true);
        } else {
            formData.append("sendEmail", true);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-invoice`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.response);
                        setUserInvoices(data.user.invoices || []);
                        setFolders(data.user.folders || []);
                        setInvoiceFile(null);
                        setSelectedFiles([]);
                        setError("");
                    } catch (e) {
                        setError("Failed to parse server response");
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.response);
                        setError(errorData.message || "Failed to send invoice");
                    } catch (e) {
                        setError(`Failed to send invoice: ${xhr.statusText || xhr.status}`);
                    }
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                setError("Network error. Please check your connection.");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const handleInvoiceSubmit = async () => {
        if (!invoiceFile) {
            alert("Please select a file");
            return;
        }
        setShowPaymentModal(true);
    };

    const handleDocumentSubmit = async () => {
        if (!documentFile) {
            alert("Please select a file");
            return;
        }

        const token = localStorage.getItem("employeeToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (documentFile instanceof FileList) {
            Array.from(documentFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-document`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.response);
                        setUserDocuments(data.user.documents || []);
                        setFolders(data.user.folders || []);
                        setDocumentFile(null);
                        setSelectedFiles([]);
                        setError("");
                    } catch (e) {
                        setError("Failed to parse server response");
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.response);
                        setError(errorData.message || "Failed to send document");
                    } catch (e) {
                        setError(`Failed to send document: ${xhr.statusText || xhr.status}`);
                    }
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                setError("Network error. Please check your connection.");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const handleRegisteredAgentSubmit = async () => {
        if (!registeredAgentFile) {
            alert("Please select a file");
            return;
        }

        const token = localStorage.getItem("employeeToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (registeredAgentFile instanceof FileList) {
            Array.from(registeredAgentFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-registered-agent-document`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.response);
                        setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
                        setFolders(data.user.folders || []);
                        setRegisteredAgentFile(null);
                        setSelectedFiles([]);
                        setError("");
                    } catch (e) {
                        setError("Failed to parse server response");
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.response);
                        setError(errorData.message || "Failed to send registered agent document");
                    } catch (e) {
                        setError(`Failed to send registered agent document: ${xhr.statusText || xhr.status}`);
                    }
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                setError("Network error. Please check your connection.");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const createFolder = async () => {
        if (!newFolderName.trim()) {
            alert("Please enter a folder name");
            return;
        }

        const token = localStorage.getItem("employeeToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/create-folder`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        folderName: newFolderName,
                        parentFolderId: currentFolder,
                        section: getActiveSection(),
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create folder");
            }

            const data = await response.json();
            setFolders(data.user.folders || []);
            setNewFolderName("");
            setShowCreateFolder(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const getActiveSection = () => TAB_SECTION_MAP[activeTab] || null;

    const getSectionFolders = (section) => folders.filter((f) => getFolderSection(f) === section);

    const openTransferModal = (item, action = "move") => {
        const section = getActiveSection() || "documents";
        setItemToMove({ ...item, sourceSection: item.sourceSection || section });
        setTransferAction(action);
        setTargetSection(section);
        setTargetFolder("");
        setShowMoveModal(true);
    };

    const openCopyFolderModal = (folder) => {
        setTargetSection(getFolderSection(folder));
        setTargetFolder("");
        setTransferAction("copy");
        setItemToMove({ type: "folder", id: folder._id, name: folder.name, sourceSection: getFolderSection(folder) });
        setShowMoveModal(true);
        setOpenFolderMenu(null);
    };

    const isItemSelected = (item) => selectedItems.some((s) => itemKey(s) === itemKey(item));

    const toggleSelectItem = (item) => {
        setSelectedItems((prev) => {
            const key = itemKey(item);
            if (prev.some((s) => itemKey(s) === key)) {
                return prev.filter((s) => itemKey(s) !== key);
            }
            return [...prev, item];
        });
    };

    const getCurrentTabFileItems = () => {
        const section = getActiveSection();
        if (activeTab === "documents") {
            return getCurrentFolderDocuments().map((d) => ({
                type: "document", id: d._id, name: d.originalName, sourceSection: section,
            }));
        }
        if (activeTab === "invoices") {
            return getCurrentFolderInvoices().map((d) => ({
                type: "invoice", id: d._id, name: d.originalName, sourceSection: section,
            }));
        }
        if (activeTab === "chat-documents") {
            return getCurrentFolderChatDocuments().map((d) => ({
                type: "chat-document", id: d._id, name: d.originalName, fileIndex: d.fileIndex, sourceSection: section,
            }));
        }
        if (activeTab === "registered-agent") {
            return getCurrentFolderRegisteredAgentDocuments().map((d) => ({
                type: "registered-agent-document", id: d._id, name: d.originalName, sourceSection: null,
            }));
        }
        return [];
    };

    const selectAllCurrentFiles = () => {
        const all = getCurrentTabFileItems();
        if (selectedItems.length === all.length && all.length > 0) {
            setSelectedItems([]);
        } else {
            setSelectedItems(all);
        }
    };

    const copySelectionToClipboard = (action) => {
        if (!selectedItems.length) {
            setError("Select one or more files first");
            return;
        }
        setClipboard({ items: selectedItems, action, sourceSection: getActiveSection() });
        setError("");
    };

    const getBatchTransferError = (items, targetSection) => {
        if (!items?.length) return null;
        if (!targetSection) return "Open Documents, Invoices, or Chat tab to paste";
        for (const item of items) {
            const allowed = SECTION_TRANSFER_RULES[item.type]?.[targetSection];
            if (!allowed) {
                const typeLabel = ITEM_TYPE_LABELS[item.type] || item.type;
                const targetLabel = SECTION_LABELS[targetSection] || targetSection;
                const sourceLabel = item.sourceSection
                    ? SECTION_LABELS[item.sourceSection]
                    : typeLabel;
                return `${typeLabel} cannot be moved to ${targetLabel}. Paste in the ${sourceLabel} tab, or use Move/Copy for cross-section transfers.`;
            }
        }
        return null;
    };

    const batchTransfer = async (items, action, targetSection, targetFolderId) => {
        if (!items?.length) return;
        const transferError = getBatchTransferError(items, targetSection);
        if (transferError) {
            setError(transferError);
            return;
        }
        const token = localStorage.getItem("employeeToken");
        setIsBatchTransferring(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/batch-transfer-items`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        items,
                        action,
                        targetSection,
                        targetFolderId: targetFolderId || null,
                    }),
                }
            );
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to transfer items");
            }
            const data = await response.json();
            applyUserData(data);
            if (targetSection === "chat" || items.some((i) => i.type === "chat-document")) {
                await fetchChatDocuments(token);
            }
            setSelectedItems([]);
            if (action === "move") setClipboard(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsBatchTransferring(false);
        }
    };

    const pasteFromClipboard = async (targetFolderId = currentFolder) => {
        if (!clipboard?.items?.length || !getActiveSection()) return;
        await batchTransfer(
            clipboard.items,
            clipboard.action,
            getActiveSection(),
            targetFolderId
        );
    };

    const handleDragStart = (e, item) => {
        const payload = isItemSelected(item)
            ? selectedItems
            : [item];
        if (!payload.length) return;
        e.dataTransfer.setData(DND_MIME, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = "copyMove";
        if (!isItemSelected(item)) {
            setSelectedItems(payload);
        }
    };

    const handleDropOnFolder = async (e, folderId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverTarget(null);
        let items = [];
        let fromDrag = false;
        try {
            const raw = e.dataTransfer.getData(DND_MIME);
            if (raw) {
                items = JSON.parse(raw);
                fromDrag = true;
            }
        } catch {
            items = [];
        }
        if (!items.length && clipboard?.items?.length) {
            items = clipboard.items;
        }
        if (!items.length || !getActiveSection()) return;
        const action = fromDrag
            ? (e.ctrlKey || e.altKey ? "copy" : "move")
            : clipboard.action;
        await batchTransfer(items, action, getActiveSection(), folderId);
    };

    const rootDropHandlers = {
        onDragOver: (e) => {
            e.preventDefault();
            setDragOverTarget("root");
        },
        onDragLeave: () => setDragOverTarget((t) => (t === "root" ? null : t)),
        onDrop: (e) => handleDropOnFolder(e, null),
    };

    const folderDropHandlers = (folderId) => ({
        onDragOver: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverTarget(folderId);
        },
        onDragLeave: (e) => {
            e.stopPropagation();
            setDragOverTarget((t) => (t === folderId ? null : t));
        },
        onDrop: (e) => handleDropOnFolder(e, folderId),
    });

    const getRowClass = (item) => {
        const selected = isItemSelected(item);
        return `border-b border-gray-100 dark:border-dark-700 text-sm cursor-grab active:cursor-grabbing ${
            selected ? "bg-primary-50 dark:bg-primary-900/20" : "hover:bg-gray-50/80 dark:hover:bg-dark-700/50"
        }`;
    };

    const renderSelectCheckbox = (item) => (
        <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            checked={isItemSelected(item)}
            onChange={() => toggleSelectItem(item)}
            onClick={(e) => e.stopPropagation()}
        />
    );

    const applyUserData = (payload) => {
        const data = payload?.user || payload;
        if (!data) return;
        setUser(data);
        setUserInvoices(data.invoices || []);
        setUserDocuments(data.documents || []);
        setRegisteredAgentDocuments(data.registeredAgentDocuments || []);
        setFolders(data.folders || []);
    };

    const moveItem = async () => {
        const token = localStorage.getItem("employeeToken");
        const sourceSection = itemToMove.sourceSection || getActiveSection();
        const isFolderCopy = itemToMove.type === "folder" && transferAction === "copy";
        const isCrossSection = targetSection !== sourceSection;
        const useTransfer = isCrossSection || transferAction === "copy" || itemToMove.type === "chat-document";

        try {
            let response;
            if (isFolderCopy) {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/copy-folder`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            userId,
                            folderId: itemToMove.id,
                            targetSection,
                            targetParentFolderId: targetFolder || null,
                        }),
                    }
                );
            } else if (useTransfer) {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/transfer-item`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            userId,
                            itemType: itemToMove.type,
                            itemId: itemToMove.id,
                            fileIndex: itemToMove.fileIndex,
                            action: transferAction,
                            targetSection,
                            targetFolderId: targetFolder || null,
                        }),
                    }
                );
            } else {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/move-item`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            userId,
                            itemType: itemToMove.type,
                            itemId: itemToMove.id,
                            targetFolderId: targetFolder || null,
                        }),
                    }
                );
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to move item");
            }

            const data = await response.json();
            applyUserData(data);
            if (itemToMove.type === "chat-document" || targetSection === "chat" || sourceSection === "chat") {
                await fetchChatDocuments(token);
            }
            setShowMoveModal(false);
            setItemToMove({ type: "", id: "", name: "" });
            setTargetFolder("");
        } catch (error) {
            setError(error.message);
        }
    };

    const renameItem = async () => {
        if (!itemToRename.id || !newItemName.trim()) return;

        const token = localStorage.getItem("employeeToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/rename-item`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        itemType: itemToRename.type,
                        itemId: itemToRename.id,
                        newName: newItemName
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to rename item");
            }

            const data = await response.json();
            if (itemToRename.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToRename.type === "document") {
                setUserDocuments(data.user.documents || []);
            } else {
                setFolders(data.user.folders || []);
            }
            setShowRenameModal(false);
            setItemToRename({ type: "", id: "", name: "" });
            setNewItemName("");
        } catch (error) {
            setError(error.message);
        }
    };

    const getFolderContentCounts = (folderId, section = getActiveSection()) => {
        const folderIdStr = folderId?.toString();
        const subfolders = folders.filter(
            (f) => f.parentFolder?.toString() === folderIdStr && getFolderSection(f) === section
        ).length;
        const documents = section === "documents"
            ? userDocuments.filter((d) => d.folderId?.toString() === folderIdStr).length
            : 0;
        const invoices = section === "invoices"
            ? userInvoices.filter((i) => i.folderId?.toString() === folderIdStr).length
            : 0;
        const chatDocs = section === "chat"
            ? chatDocuments.filter((d) => (d.folderId?.toString() || null) === folderIdStr).length
            : 0;
        const agentDocs = registeredAgentDocuments.filter((d) => d.folderId?.toString() === folderIdStr).length;
        return { subfolders, documents, invoices, chatDocs, agentDocs, total: subfolders + documents + invoices + chatDocs + agentDocs };
    };

    const openFolderDelete = (folder) => {
        const counts = getFolderContentCounts(folder._id, getFolderSection(folder));
        setItemToDelete({
            type: "folder",
            id: folder._id,
            name: folder.name,
            isSystem: folder.createdBy === "system" || SYSTEM_DEFAULT_FOLDERS.includes(folder.name),
        });
        setForceDelete(counts.total > 0);
        setShowDeleteConfirm(true);
        setOpenFolderMenu(null);
    };

    const deleteItem = async (useForce = forceDelete) => {
        const token = localStorage.getItem("employeeToken");
        try {
            let response;
            if (itemToDelete.type === "invoice") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/invoice/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "document") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/document/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "registered-agent-document") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/registered-agent-document/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "folder") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/folder/${itemToDelete.id}${useForce ? '?force=true' : ''}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete item");
            }

            const data = await response.json();
            if (itemToDelete.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToDelete.type === "document") {
                setUserDocuments(data.user.documents || []);
            } else if (itemToDelete.type === "registered-agent-document") {
                setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
            }
            setFolders(data.user.folders || []);
            setShowDeleteConfirm(false);
            setForceDelete(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const renderModal = (show, onClose, children) => {
        if (!show) return null;
        return createPortal(
            <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/50" onClick={onClose} />
                {children}
            </div>,
            document.body
        );
    };

    const openFolder = (folder) => {
        setCurrentFolder(folder._id);
        setFolderPath([...folderPath, folder]);
    };

    const navigateToFolder = (index) => {
        if (index === -1) {
            setCurrentFolder(null);
            setFolderPath([]);
        } else {
            const newPath = folderPath.slice(0, index + 1);
            setFolderPath(newPath);
            setCurrentFolder(newPath[newPath.length - 1]._id);
        }
    };

    const getCurrentFolderContents = () => {
        const section = getActiveSection();
        if (!section) return [];
        const sectionFolders = getSectionFolders(section);
        if (!currentFolder) return sectionFolders.filter((f) => !f.parentFolder);
        const currentId = currentFolder.toString();
        return sectionFolders.filter((f) => f.parentFolder?.toString() === currentId);
    };

    const getCurrentFolderDocuments = () => {
        const cur = currentFolder?.toString() || null;
        return sortByDateDesc(
            userDocuments.filter((doc) => (doc.folderId?.toString() || null) === cur)
        );
    };

    const getCurrentFolderInvoices = () => {
        const cur = currentFolder?.toString() || null;
        return sortByDateDesc(
            userInvoices.filter((inv) => (inv.folderId?.toString() || null) === cur)
        );
    };

    const getCurrentFolderChatDocuments = () => {
        const cur = currentFolder?.toString() || null;
        return sortByDateDesc(
            chatDocuments.filter((doc) => (doc.folderId?.toString() || null) === cur)
        );
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getCurrentFolderRegisteredAgentDocuments = () => {
        return sortByDateDesc(
            registeredAgentDocuments.filter((doc) => doc.folderId === currentFolder)
        );
    };

    const getAllFolders = (section = targetSection) => {
        return getSectionFolders(section).filter((f) => f._id !== currentFolder && f._id !== itemToMove.id);
    };

    const [openFolderMenu, setOpenFolderMenu] = useState(null);

    useEffect(() => {
        setCurrentFolder(null);
        setFolderPath([]);
        setOpenFolderMenu(null);
        setSelectedItems([]);
    }, [activeTab]);

    useEffect(() => {
        const onKeyDown = (e) => {
            const tag = e.target?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
                e.preventDefault();
                if (selectedItems.length) copySelectionToClipboard("copy");
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "x") {
                e.preventDefault();
                if (selectedItems.length) copySelectionToClipboard("move");
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
                e.preventDefault();
                pasteFromClipboard(currentFolder);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [selectedItems, clipboard, currentFolder, activeTab]);

    useEffect(() => {
        if (!openFolderMenu) return;
        const handleClickOutside = (e) => {
            if (e.target.closest("[data-folder-menu]")) return;
            setOpenFolderMenu(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openFolderMenu]);

    const userName = user?.name || initialUserName;

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Error Banner */}
            {error && (
                <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[1300] w-full max-w-xl animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="mx-4 p-4 bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-2xl shadow-xl flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-200 dark:bg-red-800/50 flex items-center justify-center flex-shrink-0">
                                <FaTimes className="text-red-600 dark:text-red-400" />
                            </div>
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                        <button 
                            onClick={() => setError(null)}
                            className="p-2 hover:bg-red-200/50 dark:hover:bg-red-800/50 rounded-xl transition-colors"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>
            )}
            
            {!hideBackButton && (
                <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700">
                    <FaArrowLeft /> Back
                </button>
            )}

            <div className="space-y-4 mb-4">
                {!hideBackButton && (
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Documents for <span className="text-primary-600">{userName}</span>
                    </h2>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="relative flex-1 min-w-0">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Search files and folders..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`${inputClass} pl-9`}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowCreateFolder(true)}
                        disabled={!getActiveSection()}
                        className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <FaFolderPlus size={12} /> New Folder
                    </button>
                </div>

                {/* Breadcrumb */}
                <div className="flex items-center gap-1.5 text-xs flex-wrap">
                    <button
                        type="button"
                        onClick={() => navigateToFolder(-1)}
                        {...(getActiveSection() ? rootDropHandlers : {})}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors ${!currentFolder ? "text-primary-600 font-semibold bg-primary-50 dark:bg-primary-900/20" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700"} ${dragOverTarget === "root" ? "ring-2 ring-primary-500 ring-offset-1" : ""}`}
                    >
                        <FaHome size={11} /> Root
                    </button>
                    {folderPath.map((folder, index) => (
                        <React.Fragment key={folder._id}>
                            <FaChevronRight className="text-gray-300 dark:text-gray-600 w-2.5 h-2.5" />
                            <button
                                type="button"
                                onClick={() => navigateToFolder(index)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors ${index === folderPath.length - 1 ? "text-primary-600 font-semibold bg-primary-50 dark:bg-primary-900/20" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700"}`}
                            >
                                <FaFolder size={11} /> {folder.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                {getActiveSection() && (
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-dark-900/40 border border-gray-200 dark:border-dark-600 rounded-xl">
                        <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[4.5rem]">
                            {selectedItems.length > 0 ? `${selectedItems.length} selected` : "Clipboard"}
                        </span>
                        <button
                            type="button"
                            disabled={!selectedItems.length}
                            onClick={() => copySelectionToClipboard("copy")}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-dark-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Copy (Ctrl+C)"
                        >
                            <FaCopy size={11} /> Copy
                        </button>
                        <button
                            type="button"
                            disabled={!selectedItems.length}
                            onClick={() => copySelectionToClipboard("move")}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-dark-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Cut (Ctrl+X)"
                        >
                            <FaArrowRight size={11} className="rotate-[-45deg]" /> Cut
                        </button>
                        <button
                            type="button"
                            disabled={
                                !clipboard?.items?.length
                                || isBatchTransferring
                                || !!getBatchTransferError(clipboard.items, getActiveSection())
                            }
                            onClick={() => pasteFromClipboard(currentFolder)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Paste (Ctrl+V)"
                        >
                            <FaClipboard size={11} /> Paste
                            {clipboard?.items?.length > 0 && ` (${clipboard.items.length})`}
                        </button>
                        {isBatchTransferring && (
                            <span className="text-xs text-primary-600 animate-pulse">Transferring…</span>
                        )}
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto hidden sm:inline">
                            Drag files onto folders · Ctrl+drag to copy · Ctrl+C / Ctrl+X / Ctrl+V
                        </span>
                    </div>
                )}

                {/* Tab switcher */}
                <div className="inline-flex flex-wrap gap-1 p-1 bg-gray-100 dark:bg-dark-900/50 rounded-xl border border-gray-200 dark:border-dark-600">
                    <button type="button" onClick={() => setActiveTab("documents")} className={tabBtnClass(activeTab === "documents")}>
                        <FaFileAlt size={11} /> Documents
                    </button>
                    <button type="button" onClick={() => setActiveTab("invoices")} className={tabBtnClass(activeTab === "invoices")}>
                        <FaFileInvoice size={11} /> Invoices
                    </button>
                    <button type="button" onClick={() => setActiveTab("chat-documents")} className={tabBtnClass(activeTab === "chat-documents")}>
                        <FaComments size={11} /> Chat
                    </button>
                    <button type="button" onClick={() => setActiveTab("registered-agent")} className={tabBtnClass(activeTab === "registered-agent")}>
                        <FaUserShield size={11} /> Agent
                    </button>
                </div>
            </div>

            {/* Folders Section */}
            {getActiveSection() && (
                <div className={`${folderCardClass} mb-4`}>
                    <div className={`${cardBodyClass} pb-2 border-b border-gray-200 dark:border-dark-600`}>
                        <span className={`${labelClass} flex items-center gap-1.5`}>
                            <FaFolder className="text-yellow-500" /> Folders · {SECTION_LABELS[getActiveSection()]}
                        </span>
                    </div>
                    {getCurrentFolderContents().length > 0 ? (
                    <div className={`${cardBodyClass} grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 overflow-visible`}>
                        {getCurrentFolderContents().map((folder) => (
                            <div
                                key={folder._id}
                                {...folderDropHandlers(folder._id)}
                                className={`relative bg-white dark:bg-dark-800 border rounded-xl p-3 flex flex-col items-center text-center hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all cursor-pointer group overflow-visible ${openFolderMenu === folder._id ? "z-30" : "z-0"} ${dragOverTarget === folder._id ? "border-primary-500 ring-2 ring-primary-400 bg-primary-50/50 dark:bg-primary-900/20" : "border-gray-200 dark:border-dark-600"}`}
                            >
                                <div className="relative w-full flex justify-end overflow-visible" data-folder-menu>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setOpenFolderMenu(openFolderMenu === folder._id ? null : folder._id); }}
                                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors bg-white/80 dark:bg-dark-800/80"
                                        title="Folder options"
                                    >
                                        <FaEllipsisV className="w-3 h-3" />
                                    </button>
                                    {openFolderMenu === folder._id && (
                                        <div className="absolute right-0 top-8 w-40 bg-white dark:bg-dark-800 rounded-lg shadow-xl border border-gray-200 dark:border-dark-600 z-[100] py-1">
                                            <button type="button" onClick={(e) => { e.stopPropagation(); setItemToRename({ type: "folder", id: folder._id, name: folder.name }); setNewItemName(folder.name); setShowRenameModal(true); setOpenFolderMenu(null); }} className="w-full px-3 py-2 text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"><FaEdit /> Rename</button>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); openTransferModal({ type: "folder", id: folder._id, name: folder.name, sourceSection: getFolderSection(folder) }, "move"); setOpenFolderMenu(null); }} className="w-full px-3 py-2 text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"><FaArrowRight /> Move</button>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); openCopyFolderModal(folder); }} className="w-full px-3 py-2 text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"><FaFolder /> Copy</button>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); openFolderDelete(folder); }} className="w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"><FaTrashAlt /> Delete</button>
                                        </div>
                                    )}
                                </div>
                                <FaFolder size={36} className="text-yellow-500 mb-2" onClick={() => openFolder(folder)} />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 line-clamp-2 w-full" onClick={() => openFolder(folder)}>{folder.name}</span>
                            </div>
                        ))}
                    </div>
                    ) : (
                        <div className={`${cardBodyClass} py-8 text-center text-sm text-gray-500 dark:text-gray-400`}>
                            No folders in {SECTION_LABELS[getActiveSection()]}. Click <strong>New Folder</strong> to create one.
                        </div>
                    )}
                </div>
            )}

            {/* Documents Section */}
            {activeTab === "documents" && (
                <>
                    <div className={`${cardClass} mb-4`}>
                        <div className={`${cardBodyClass} pb-3 border-b border-gray-200 dark:border-dark-600`}>
                            <span className={`${labelClass} flex items-center gap-1.5`}>
                                <FaUpload /> Upload Documents
                            </span>
                        </div>
                        <div className={cardBodyClass}>
                            <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'document')}
                                    multiple
                                    disabled={isUploading}
                                    className={`${inputClass} flex-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-primary-600 file:text-white file:text-xs file:font-semibold file:cursor-pointer disabled:opacity-50`}
                                />
                                <button
                                    type="button"
                                    onClick={handleDocumentSubmit}
                                    disabled={!documentFile || isUploading}
                                    className="shrink-0 px-4 py-2.5 text-sm font-semibold bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                                >
                                    {isUploading ? "Uploading..." : "Upload"}
                                </button>
                            </div>
                            {selectedFiles.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Selected Files:</h6>
                                    <div className="space-y-2">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                                <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><FaFileAlt className="text-gray-400" /> {file.name}</span>
                                                <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-dark-600 text-gray-600 dark:text-gray-400 rounded">{formatFileSize(file.size)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {isUploading && (
                                <div className="mt-3">
                                    <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Uploading...</span><span>{uploadProgress}%</span></div>
                                    <div className="h-1.5 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden"><div className="h-full bg-primary-600 transition-all rounded-full" style={{ width: `${uploadProgress}%` }} /></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={cardClass}>
                        <div className={`${cardBodyClass} pb-3 border-b border-gray-200 dark:border-dark-600`}>
                            <span className={`${labelClass} flex items-center gap-1.5`}>
                                <FaFileAlt /> Documents
                            </span>
                        </div>
                        {getCurrentFolderDocuments().length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-dark-700">
                                        <tr>
                                            <th className="w-10 px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    checked={getCurrentTabFileItems().length > 0 && selectedItems.length === getCurrentTabFileItems().length}
                                                    onChange={selectAllCurrentFiles}
                                                />
                                            </th>
                                            <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Name</th>
                                            <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Size</th>
                                            <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</th>
                                            <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCurrentFolderDocuments().map((document) => {
                                            const item = { type: "document", id: document._id, name: document.originalName, sourceSection: getActiveSection() };
                                            return (
                                            <tr
                                                key={document._id}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, item)}
                                                className={getRowClass(item)}
                                            >
                                                <td className="px-3 py-2">{renderSelectCheckbox(item)}</td>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                                    <span className="flex items-center gap-2 min-w-0"><FaFileAlt className="text-gray-400 shrink-0" /><span className="truncate">{document.originalName}</span></span>
                                                </td>
                                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{formatFileSize(document.size || 0)}</td>
                                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{new Date(document.date).toLocaleDateString()}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => setPreviewFile({
                                                                url: document.fileUrl,
                                                                name: document.originalName,
                                                                size: document.size,
                                                                type: document.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                            })}
                                                            className="p-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <a href={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20" title="Download"><FaFileDownload /></a>
                                                        <button onClick={() => openTransferModal({ type: "document", id: document._id, name: document.originalName })} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700" title="Move / copy"><FaArrowRight /></button>
                                                        <button onClick={() => { setItemToDelete({ type: "document", id: document._id }); setShowDeleteConfirm(true); }} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20" title="Delete item"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );})}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <FaFileAlt className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                                <p className="text-gray-500">No documents in this folder</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Invoices Section */}
            {activeTab === "invoices" && (
                <>
                    <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUpload /> Upload Invoices</h6>
                        </div>
                        <div className="p-4">
                            <input type="file" onChange={(e) => handleFileChange(e, 'invoice')} multiple disabled={isUploading} className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-100 file:text-primary-700" />
                            {selectedFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                            <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><FaFileInvoice className="text-primary-500" /> {file.name}</span>
                                            <span className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded">{formatFileSize(file.size)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isUploading && (
                                <div className="mt-4">
                                    <div className="h-2 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden"><div className="h-full bg-primary-600 transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                                </div>
                            )}
                            <button onClick={handleInvoiceSubmit} disabled={!invoiceFile || isUploading} className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">
                                {isUploading ? 'Uploading...' : <><FaUpload /> Upload Invoices</>}
                            </button>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaFileInvoice /> Invoices</h6>
                        </div>
                        {getCurrentFolderInvoices().length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-dark-700">
                                        <tr>
                                            <th className="w-10 px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    checked={getCurrentTabFileItems().length > 0 && selectedItems.length === getCurrentTabFileItems().length}
                                                    onChange={selectAllCurrentFiles}
                                                />
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCurrentFolderInvoices().map((invoice) => {
                                            const item = { type: "invoice", id: invoice._id, name: invoice.originalName, sourceSection: getActiveSection() };
                                            return (
                                            <tr
                                                key={invoice._id}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, item)}
                                                className={getRowClass(item)}
                                            >
                                                <td className="px-4 py-3">{renderSelectCheckbox(item)}</td>
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaFileInvoice className="text-primary-500" /> {invoice.originalName}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(invoice.size || 0)}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(invoice.date).toLocaleDateString()}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => setPreviewFile({
                                                                url: invoice.fileUrl,
                                                                name: invoice.originalName,
                                                                size: invoice.size,
                                                                type: invoice.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                            })}
                                                            className="p-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <a href={`${process.env.REACT_APP_API_BASE_URL}${invoice.fileUrl}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"><FaFileDownload /></a>
                                                        <button onClick={() => openTransferModal({ type: "invoice", id: invoice._id, name: invoice.originalName })} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"><FaArrowRight /></button>
                                                        <button onClick={() => { setItemToDelete({ type: "invoice", id: invoice._id }); setShowDeleteConfirm(true); }} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );})}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <FaFileInvoice className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                                <p className="text-gray-500">No invoices in this folder</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Registered Agent Documents Section */}
            {activeTab === "registered-agent" && (
                <>
                    <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUpload /> Upload Registered Agent Documents</h6>
                        </div>
                        <div className="p-4">
                            <input type="file" onChange={(e) => handleFileChange(e, 'registered-agent')} multiple disabled={isUploading} className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-yellow-100 file:text-yellow-700" />
                            {selectedFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                            <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><FaUserShield className="text-yellow-500" /> {file.name}</span>
                                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">{formatFileSize(file.size)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isUploading && (
                                <div className="mt-4">
                                    <div className="h-2 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden"><div className="h-full bg-yellow-500 transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                                </div>
                            )}
                            <button onClick={handleRegisteredAgentSubmit} disabled={!registeredAgentFile || isUploading} className="mt-4 flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white font-medium rounded-xl hover:bg-yellow-600 disabled:opacity-50">
                                {isUploading ? 'Uploading...' : <><FaUpload /> Upload Registered Agent Documents</>}
                            </button>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUserShield /> Registered Agent Documents</h6>
                        </div>
                        {getCurrentFolderRegisteredAgentDocuments().length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-dark-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCurrentFolderRegisteredAgentDocuments().map((document, index) => (
                                            <tr key={index} className="border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700">
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaUserShield className="text-yellow-500" /> {document.originalName}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(document.size || 0)}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(document.date).toLocaleDateString()}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => setPreviewFile({
                                                                url: document.fileUrl,
                                                                name: document.originalName,
                                                                size: document.size,
                                                                type: document.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                            })}
                                                            className="p-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <a href={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"><FaFileDownload /></a>
                                                        <button onClick={() => { setItemToMove({ type: "registered-agent-document", id: document._id, name: document.originalName }); setShowMoveModal(true); }} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"><FaArrowRight /></button>
                                                        <button onClick={() => { setItemToDelete({ type: "registered-agent-document", id: document._id }); setShowDeleteConfirm(true); }} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <FaUserShield className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                                <p className="text-gray-500">No registered agent documents in this folder</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Chat Documents Section */}
            {activeTab === "chat-documents" && (
                <div className={`${cardClass}`}>
                    <div className={`${cardBodyClass} pb-3 border-b border-gray-200 dark:border-dark-600`}>
                        <span className={`${labelClass} flex items-center gap-1.5`}>
                            <FaComments /> Chat Documents
                        </span>
                    </div>
                    {getCurrentFolderChatDocuments().length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-dark-700">
                                    <tr>
                                        <th className="w-10 px-4 py-3">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                checked={getCurrentTabFileItems().length > 0 && selectedItems.length === getCurrentTabFileItems().length}
                                                onChange={selectAllCurrentFiles}
                                            />
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getCurrentFolderChatDocuments().map((document) => {
                                        const item = { type: "chat-document", id: document._id, name: document.originalName, fileIndex: document.fileIndex, sourceSection: "chat" };
                                        return (
                                        <tr
                                            key={document._id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, item)}
                                            className={getRowClass(item)}
                                        >
                                            <td className="px-4 py-3">{renderSelectCheckbox(item)}</td>
                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaFileAlt className="text-cyan-500" /> {document.originalName}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(document.size || 0)}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(document.timestamp).toLocaleDateString()}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => setPreviewFile({
                                                            url: document.fileUrl?.startsWith("/") ? document.fileUrl : `/uploads/chat/${document.fileUrl?.split("/").pop()}`,
                                                            name: document.originalName,
                                                            size: document.size,
                                                            type: document.originalName?.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg"
                                                        })}
                                                        className="p-2 text-cyan-600 border border-cyan-300 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                                                        title="Preview"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <a href={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl?.startsWith("/") ? document.fileUrl : `/uploads/chat/${document.fileUrl?.split("/").pop()}`}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 inline-flex" title="Download"><FaFileDownload /></a>
                                                    <button onClick={() => openTransferModal({ type: "chat-document", id: document._id, name: document.originalName, fileIndex: document.fileIndex, sourceSection: "chat" })} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50" title="Move / copy"><FaArrowRight /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );})}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <FaComments className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                            <p className="text-gray-500">No chat documents in this folder</p>
                        </div>
                    )}
                </div>
            )}

            {/* Create Folder Modal */}
            {renderModal(showCreateFolder, () => setShowCreateFolder(false), (
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create New Folder</h2>
                            <button onClick={() => setShowCreateFolder(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Folder Name</label>
                            <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Enter folder name" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => setShowCreateFolder(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={createFolder} className="px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Create Folder</button>
                        </div>
                    </div>
            ))}

            {/* Rename Modal */}
            {renderModal(showRenameModal, () => setShowRenameModal(false), (
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Rename Item</h2>
                            <button onClick={() => setShowRenameModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">New Name</label>
                            <input type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Enter new name" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => setShowRenameModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={renameItem} className="px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Rename</button>
                        </div>
                    </div>
            ))}

            {/* Delete Confirmation Modal */}
            {renderModal(showDeleteConfirm, () => { setShowDeleteConfirm(false); setForceDelete(false); }, (
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Confirm Delete</h2>
                            <button onClick={() => { setShowDeleteConfirm(false); setForceDelete(false); }} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 dark:text-gray-300 mb-4">
                                {itemToDelete.type === "folder" && itemToDelete.name
                                    ? `Delete folder "${itemToDelete.name}"?`
                                    : `Are you sure you want to remove this ${itemToDelete.type?.replace(/-/g, " ")}?`}
                                {" "}This action cannot be undone.
                            </p>

                            {itemToDelete.type === "folder" && (() => {
                                const counts = getFolderContentCounts(itemToDelete.id);
                                if (counts.total === 0) return null;
                                const parts = [];
                                if (counts.documents) parts.push(`${counts.documents} document${counts.documents > 1 ? "s" : ""}`);
                                if (counts.invoices) parts.push(`${counts.invoices} invoice${counts.invoices > 1 ? "s" : ""}`);
                                if (counts.chatDocs) parts.push(`${counts.chatDocs} chat file${counts.chatDocs > 1 ? "s" : ""}`);
                                if (counts.agentDocs) parts.push(`${counts.agentDocs} agent file${counts.agentDocs > 1 ? "s" : ""}`);
                                if (counts.subfolders) parts.push(`${counts.subfolders} subfolder${counts.subfolders > 1 ? "s" : ""}`);
                                return (
                                    <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl px-3 py-2 mb-4">
                                        This folder is not empty ({parts.join(", ")}). Enable <strong>Recursive Delete</strong> below to remove everything inside.
                                    </p>
                                );
                            })()}

                            {itemToDelete.type === "folder" && itemToDelete.isSystem && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-700/50 border border-gray-200 dark:border-dark-600 rounded-xl px-3 py-2 mb-4">
                                    This is a default system folder. After deletion it will stay removed and will not be recreated automatically.
                                </p>
                            )}

                            {itemToDelete.type === "folder" && (
                                <div className="p-4 bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-800/50 rounded-2xl">
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <div className="relative flex items-center mt-0.5">
                                            <input
                                                type="checkbox"
                                                className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-300 dark:border-dark-600 transition-all checked:bg-primary-600 checked:border-primary-600 focus:outline-none"
                                                checked={forceDelete}
                                                onChange={(e) => setForceDelete(e.target.checked)}
                                            />
                                            <svg className="absolute h-3.5 w-3.5 left-0.5 top-0.5 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity text-white" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 5.917L5.724 10.5L16 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">Recursive Delete</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 italic">Delete all files and sub-folders inside this folder automatically.</span>
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => { setShowDeleteConfirm(false); setForceDelete(false); }} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button
                                onClick={() => deleteItem(forceDelete)}
                                disabled={itemToDelete.type === "folder" && getFolderContentCounts(itemToDelete.id).total > 0 && !forceDelete}
                                className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-200 dark:shadow-none"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
            ))}

            {renderModal(showMoveModal, () => setShowMoveModal(false), (
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                {transferAction === "copy" ? "Copy" : "Move"} {itemToMove.type?.replace(/-/g, " ")}
                            </h2>
                            <button onClick={() => setShowMoveModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-gray-700 dark:text-gray-300 text-sm">
                                <strong>{itemToMove.name}</strong>
                            </p>
                            {itemToMove.type !== "folder" && (
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setTransferAction("move")} className={`flex-1 py-2 text-xs font-semibold rounded-lg border ${transferAction === "move" ? "bg-primary-600 text-white border-primary-600" : "border-gray-200 dark:border-dark-600 text-gray-600"}`}>Move</button>
                                    <button type="button" onClick={() => setTransferAction("copy")} className={`flex-1 py-2 text-xs font-semibold rounded-lg border ${transferAction === "copy" ? "bg-primary-600 text-white border-primary-600" : "border-gray-200 dark:border-dark-600 text-gray-600"}`}>Copy</button>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Destination section</label>
                                <select value={targetSection} onChange={(e) => { setTargetSection(e.target.value); setTargetFolder(""); }} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                                    <option value="documents">Documents</option>
                                    <option value="invoices">Invoices</option>
                                    <option value="chat">Chat</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Destination folder</label>
                                <select value={targetFolder} onChange={(e) => setTargetFolder(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                                    <option value="">Root</option>
                                    {getAllFolders(targetSection).map((folder) => (
                                        <option key={folder._id} value={folder._id}>{folder.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => setShowMoveModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={moveItem} className="px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">
                                {transferAction === "copy" ? "Copy" : "Move"}
                            </button>
                        </div>
                    </div>
            ))}

            {/* File Preview Modal */}
            {previewFile && (
                <FilePreviewModal
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            )}

            {/* Payment Details Modal */}
            <PaymentDetailsModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                onSubmit={(details) => performInvoiceUpload(details)}
                onSkip={(sendEmail) => performInvoiceUpload({ sendEmail })}
                userName={userName}
                isLoading={isUploading}
            />
        </div>
    );
};

export default DocumentsManager;
